/**
 * @fileoverview
 * Definición de las rutas principales de la aplicación usando React Router v6.
 * Las rutas están protegidas por el componente <PrivateRoute />, que valida los roles permitidos.
 *
 * Rutas disponibles:
 * - Dashboard
 * - Administración (Usuarios, Candidatos, Colegios, Preguntas)
 * - Examinador (Resultados)
 * - Certificación (Indicaciones, Exámenes, Resultados)
 * - Páginas de error (403, 404), login
 */

import React from 'react';
import App from './../app.jsx';
import { Navigate } from 'react-router-dom';

import PrivateRoute from 'components/PrivateRoute.jsx';
import LoginPage from 'pages/login/Login';

import Dashboard from 'pages/dashboard/dashboard';
import Users from 'pages/administration/users';
import Candidates from 'pages/administration/candidates/index.js';
import Colleges from 'pages/administration/colleges/index.js';
import QuestionsEPSt from 'pages/questions/epst';
import QuestionsEMeT from 'pages/questions/emet';
import ResultsEPSt from 'pages/results/epst';
import ResultsEMeT from 'pages/results/emet';
import Indications from 'pages/certifications/indications';
import CertificationEmet from 'pages/certifications/emet';
import CertificationEpst from 'pages/certifications/epst';
import CertificationResultEmet from 'pages/certifications/emet/result';
import CertificationResultEpst from 'pages/certifications/epst/result';
import EvaluationEpst from 'pages/results/epst/evaluation/index.js';
import ViewEpst from 'pages/results/epst/view-exam/index.js';
import ViewEmet from 'pages/results/emet/view-exam/index.js';

import Error403 from 'pages/errors/Error403.jsx';
import Error404 from 'pages/errors/Error404';

import { MASTER, ADMINISTRATOR, EXAMINER, CANDIDATE } from 'utils/roles.js';

/**
 * @constant AppRoute
 * @description
 * Arreglo con la estructura de rutas de la aplicación.
 * Utiliza el layout principal <App /> como envoltorio.
 */
const AppRoute = [{
	path: '*',
	element: <App />,
	children: [
		{ path: '', element: <Navigate to='/dashboard' /> },

		// Dashboard
		{
			path: 'dashboard',
			element: (
				<PrivateRoute requiredRoles={[MASTER, ADMINISTRATOR, EXAMINER, CANDIDATE]}>
					<Dashboard />
				</PrivateRoute>
			)
		},

		// Administración
		{
			path: 'admin/*',
			children: [
				{
					path: 'users',
					element: (
						<PrivateRoute requiredRoles={[MASTER, ADMINISTRATOR]}>
							<Users title="Usuarios" subtitle="CertifiED" />
						</PrivateRoute>
					)
				},
				{
					path: 'candidates',
					element: (
						<PrivateRoute requiredRoles={[MASTER, ADMINISTRATOR]}>
							<Candidates title="Candidatos" subtitle="CertifiED" />
						</PrivateRoute>
					)
				},
				{
					path: 'colleges',
					element: (
						<PrivateRoute requiredRoles={[MASTER, ADMINISTRATOR]}>
							<Colleges />
						</PrivateRoute>
					)
				},
				{
					path: 'questions/epst',
					element: (
						<PrivateRoute requiredRoles={[MASTER, ADMINISTRATOR]}>
							<QuestionsEPSt title="Preguntas" subtitle="EPSt" />
						</PrivateRoute>
					)
				},
				{
					path: 'questions/emet',
					element: (
						<PrivateRoute requiredRoles={[MASTER, ADMINISTRATOR]}>
							<QuestionsEMeT title="Preguntas" subtitle="EMeT" />
						</PrivateRoute>
					)
				}
			]
		},

		// Examinador
		{
			path: 'examiner/*',
			children: [
				{
					path: 'results/epst',
					element: (
						<PrivateRoute requiredRoles={[MASTER, EXAMINER]}>
							<ResultsEPSt title="Resultados" subtitle="EPSt" />
						</PrivateRoute>
					)
				},
				{
					path: 'evaluate/epst',
					element: (
						<PrivateRoute requiredRoles={[MASTER, EXAMINER]}>
							<EvaluationEpst title="Evaluacion" subtitle="EPSt" />
						</PrivateRoute>
					)
				},
				{
					path: 'view/epst',
					element: (
						<PrivateRoute requiredRoles={[MASTER, EXAMINER]}>
							<ViewEpst title="Certificación" subtitle="EPSt" />
						</PrivateRoute>
					)
				},
				{
					path: 'results/emet',
					element: (
						<PrivateRoute requiredRoles={[MASTER, EXAMINER]}>
							<ResultsEMeT title="Resultados" subtitle="EMeT" />
						</PrivateRoute>
					)
				},
				{
					path: 'view/emet',
					element: (
						<PrivateRoute requiredRoles={[MASTER, EXAMINER]}>
							<ViewEmet title="Certificación" subtitle="EMeT" />
						</PrivateRoute>
					)
				},
			]
		},

		// Certificación
		{
			path: 'certification/*',
			children: [
				{
					path: 'indications',
					element: (
						<PrivateRoute requiredRoles={[CANDIDATE]}>
							<Indications title="Certificación" subtitle="Indicaciones" />
						</PrivateRoute>
					)
				},
				{
					path: 'emet',
					element: (
						<PrivateRoute requiredRoles={[CANDIDATE]}>
							<CertificationEmet title="Certificación" subtitle="EMeT" />
						</PrivateRoute>
					)
				},
				{
					path: 'epst',
					element: (
						<PrivateRoute requiredRoles={[CANDIDATE]}>
							<CertificationEpst title="Certificación" subtitle="EPSt" />
						</PrivateRoute>
					)
				},
				{
					path: 'result/emet',
					element: (
						<PrivateRoute requiredRoles={[CANDIDATE]}>
							<CertificationResultEmet title="Certificación" subtitle="EMeT" />
						</PrivateRoute>
					)
				},
				{
					path: 'result/epst',
					element: (
						<PrivateRoute requiredRoles={[CANDIDATE]}>
							<CertificationResultEpst title="Certificación" subtitle="EPSt" />
						</PrivateRoute>
					)
				}
			]
		},

		// Errores y login
		{
			path: 'pages/*',
			children: [
				{ path: 'forbidden', element: <Error403 /> },
				{ path: 'error', element: <Error404 /> },
				{ path: 'login', element: <LoginPage /> },
				{ path: '*', element: <Navigate to="/pages/error" replace /> }
			]
		},

		// Fallback para rutas no definidas
		{ path: '*', element: <Navigate to="/pages/error" replace /> }
	]
}];

export default AppRoute;